import { useState, useEffect } from 'react';
import ArcadeBackground from './ArcadeBackground';
import SiteCard from './SiteCard';
import SettingsModal from './SettingsModal';
import AboutMe from './AboutMe';

// ── Content imports — edit these files, not this file ──────────────────────
import homeContent from './content/home.json';
import projectsData from './content/projects.json';
import { ICONS } from './icons.jsx';

const VALID_BACKGROUNDS = ['none', 'arcade'];
const DEFAULT_BACKGROUND = 'arcade';

// Helper to determine initial tab / view from URL path
const getTabFromPath = (path) => {
  const cleanPath = path.toLowerCase().replace(/\/$/, '');
  if (cleanPath === '/about'    || cleanPath.startsWith('/about/'))    return 'about-standalone';
  if (cleanPath === '/projects' || cleanPath.startsWith('/projects/')) return 'projects';
  if (cleanPath === '/aboutme'  || cleanPath.startsWith('/aboutme/'))  return 'aboutme';
  return 'home';
};

const TAB_TITLES = {
  home: 'Home',
  projects: 'Projects',
  aboutme: 'About Me',
  'about-standalone': 'About Me'
};
const getTitle = (tab) => TAB_TITLES[tab] ?? tab.charAt(0).toUpperCase() + tab.slice(1);

export default function App() {
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(window.location.pathname));
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Accessibility — persisted to localStorage
  const [accessibility, setAccessibility] = useState(() => {
    try {
      const saved = localStorage.getItem('accessibility');
      return saved ? JSON.parse(saved) : { reduceMotion: false };
    } catch {
      return { reduceMotion: false };
    }
  });

  useEffect(() => {
    localStorage.setItem('accessibility', JSON.stringify(accessibility));
    document.documentElement.classList.toggle('reduce-motion', accessibility.reduceMotion);
  }, [accessibility]);

  // Tab navigation — updates URL path and document.title
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const path = tab === 'home' ? '/' : `/${tab}`;
    if (window.location.pathname !== path) {
      window.history.pushState({ tab }, '', path);
    }
    document.title = getTitle(tab);
  };

  // Sync with browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const tab = getTabFromPath(window.location.pathname);
      setActiveTab(tab);
      document.title = getTitle(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Set title on mount
  useEffect(() => { document.title = getTitle(activeTab); }, [activeTab]);

  // Background — persisted to localStorage, invalid values fall back to default
  const [backgroundType, setBackgroundType] = useState(() => {
    const saved = localStorage.getItem('backgroundType');
    if (saved && VALID_BACKGROUNDS.includes(saved)) return saved;
    localStorage.setItem('backgroundType', DEFAULT_BACKGROUND);
    return DEFAULT_BACKGROUND;
  });

  useEffect(() => {
    if (VALID_BACKGROUNDS.includes(backgroundType)) {
      localStorage.setItem('backgroundType', backgroundType);
    } else {
      setBackgroundType(DEFAULT_BACKGROUND);
      localStorage.setItem('backgroundType', DEFAULT_BACKGROUND);
    }
  }, [backgroundType]);

  // Resolve icon SVGs from the icon registry
  const projects = projectsData.map(p => ({
    ...p,
    iconSvg: ICONS[p.icon] ?? null,
  }));

  const isStandaloneAbout = activeTab === 'about-standalone';

  // ── Standalone View (retorded.org/about) — Pure AboutMe component without Navbar or Footer
  if (isStandaloneAbout) {
    return (
      <>
        {backgroundType === 'arcade' && <ArcadeBackground />}
        <main className="main-container">
          <AboutMe />
        </main>
      </>
    );
  }

  // ── Mainhub Portal Layout (Home / Projects / AboutMe Tab)
  return (
    <>
      {/* Background engine */}
      {backgroundType === 'arcade' && <ArcadeBackground />}

      {/* Navigation */}
      <header className="navbar">
        <div className="nav-left">
          <a
            href="/"
            className="nav-brand"
            onClick={(e) => { e.preventDefault(); handleTabChange('home'); }}
          >
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 800, fontSize: '0.85rem',
            }}>
              R
            </div>
            <span className="brand-logo-text gradient-text">retorded.org</span>
          </a>

          <nav className="nav-tabs">
            {['home', 'projects', 'aboutme'].map(tab => (
              <button
                key={tab}
                className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => handleTabChange(tab)}
              >
                {TAB_TITLES[tab]}
              </button>
            ))}
          </nav>
        </div>

        <div className="nav-controls">
          <button
            className="hamburger-btn"
            onClick={() => setIsSettingsOpen(true)}
            title="Settings"
            aria-label="Settings"
          >
            &#9776;
          </button>
        </div>
      </header>

      {/* Settings modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        backgroundType={backgroundType}
        setBackgroundType={setBackgroundType}
        accessibility={accessibility}
        setAccessibility={setAccessibility}
      />

      {/* Page content */}
      <main className="main-container">

        {activeTab === 'home' && (
          <section className="hero-section">
            <h1 className="hero-title">
              {homeContent.greeting}{' '}
              <span className="gradient-text">{homeContent.name}</span>
            </h1>
            <p className="hero-subtitle">{homeContent.subtitle}</p>
          </section>
        )}

        {activeTab === 'projects' && (
          <section>
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800 }}>Projects</h2>
            </div>
            <div className="sites-grid">
              {projects.map(project => (
                <SiteCard key={project.id} {...project} />
              ))}
            </div>
          </section>
        )}

        {activeTab === 'aboutme' && <AboutMe />}

      </main>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} retorded.org</p>
      </footer>
    </>
  );
}
